import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Stack,
  TextField,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import BlockIcon from "@mui/icons-material/Block";
import DownloadIcon from "@mui/icons-material/Download";
import AssignmentIcon from "@mui/icons-material/Assignment";

import ActionMenu from "../../../../components/ActionMenu";
import DataGrid from "../../../../components/DataGrid";
import HrmEmployeeTaxPayerDetailDrawer from "../drawers/detail";
import HrmEmployeeTerminationDrawer from "../drawers/termination";
import HrmEmployeeVacationDrawer from "../drawers/vacation";
import HrmEmployeeAbsenceDrawer from "../drawers/absence";

import useAuthStore from "../../../../stores/auth";
import useNavStore from "../../../../stores/nav";
import useHrmStore from "../../../../stores/hrManagement";
import useHrmTerminationStore from "../../../../stores/hrTermination";
import useHrmVacationStore from "../../../../stores/hrVacation";
import useHrmAbsenceStore from "../../../../stores/hrAbsence";
import { usePDF } from "@react-pdf/renderer";

import { fetchDominioData } from "../../../../fetchDominioData";
import { formatDate } from "../../../../utils";
import MyDocument from "../../../../components/PdfDocument";

function HRManagementEmployees() {
  const company = useAuthStore((state) => state.company);
  const drawer = useHrmStore((state) => state.toggleDrawer);
  const terminationDrawer = useHrmTerminationStore(
    (state) => state.toggleDrawer,
  );
  const vacationDrawer = useHrmVacationStore((state) => state.toggleDrawer);
  const absenceDrawer = useHrmAbsenceStore((state) => state.toggleDrawer);
  const [progress, setProgress] = useState(false);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const exportData = useNavStore((state) => state.setExportData);
  const isSearching = search !== appliedSearch;
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAppliedSearch(search);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const data = async (limit: number, skip: number) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    setProgress(true);

    try {
      const searchParam = appliedSearch.trim()
        ? `&like=nome|${encodeURIComponent(appliedSearch.trim())}`
        : "";
      const response = await fetchDominioData(
        `/empresa/${company.reference}/empregado?limit=${limit}&start=${
          skip + 1
        }${searchParam}`,
        {
          method: "GET",
        },
      );

      if (!response) {
        return;
      }

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setRowCount(response.count);
      setRows(response.rows);
      exportData(
        response.rows.map((row: any) => ({
          codigo: row.i_empregados,
          nome: row.nome,
          data_admissao: formatDate(row.admissao, "D/M/Y"),
        })),
      );
    } catch (error) {
      console.log(error);

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setError(true);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setProgress(false);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: "i_empregados",
      sortable: false,
      headerName: "Número",
      renderCell: (params: any) => {
        return params.row.i_empregados;
      },
    },
    {
      field: "nome",
      sortable: false,
      headerName: "Nome",
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Link component="button" onClick={() => drawer(params.row)}>
            {params.row.nome}
          </Link>
        );
      },
    },
    {
      field: "admissao",
      sortable: false,
      headerName: "Admissão",
      renderCell: (params: any) => {
        return formatDate(params.row.admissao, "D/M/Y");
      },
    },
    {
      field: "id",
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [instance] = usePDF({
          document: (
            <MyDocument
              name={params.row.nome}
              document={params.row.cpf}
              local={`${params.row.cidade ? params.row.cidade : "Cidade"}, ${
                params.row.estado ? params.row.estado : "Estado"
              }`}
            />
          ),
        });

        const items = [
          {
            title: "Solicitar Férias",
            icon: <TaskAltIcon />,
            onClick: () => vacationDrawer(params.row),
          },
          {
            title: "Solicitar Rescisão",
            icon: <BlockIcon />,
            onClick: () => terminationDrawer(params.row),
          },
          {
            title: "Atestados & Afastamentos",
            icon: <AssignmentIcon />,
            onClick: () => absenceDrawer(params.row),
          },
          {
            title: "Declaração Étnico-Racial",
            icon: <DownloadIcon />,
            onClick: () => {
              if (instance.url) {
                window.open(instance.url);
              } else {
                console.error("URL não está disponível");
              }
            },
          },
        ];
        return <ActionMenu items={items} />;
      },
      renderHeader: () => {
        return <MoreHorizIcon />;
      },
    },
  ];

  if (error) {
    return (
      <Alert severity="error">
        Ops, no momento não foi possível realizar a listagem de empregados.
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={2} sx={{ p: 2 }}>
        <TextField
          fullWidth
          label="Buscar por nome"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
        />
        <Box sx={{ position: "relative", minHeight: 200 }}>
          <Box
            sx={{
              opacity: isSearching || progress ? 0 : 1,
              transition: "opacity 0.2s",
            }}
          >
            <DataGrid
              key={appliedSearch}
              rows={rows}
              data={data}
              loading={false}
              columns={columns}
              rowCount={rowCount}
            />
          </Box>
          {(isSearching || progress) && (
            <Stack
              alignItems="center"
              justifyContent="flex-start"
              sx={{ position: "absolute", inset: 0, pt: 2 }}
            >
              <CircularProgress />
            </Stack>
          )}
        </Box>
      </Stack>
      <HrmEmployeeTaxPayerDetailDrawer />
      <HrmEmployeeTerminationDrawer />
      <HrmEmployeeVacationDrawer />
      <HrmEmployeeAbsenceDrawer />
    </>
  );
}

export default HRManagementEmployees;
